import React, { useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";
import axios from "axios";
import PageWrapper from "../components/PageWrapper/PageWrapper";
import SingleProduct from "../components/products/SingleProduct";
import BackButton from "../components/BackButton";
import IconButton from "@mui/material/IconButton";
import SortIcon from "@mui/icons-material/Sort";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward"; // For Price Low to High
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SortByAlphaIcon from "@mui/icons-material/SortByAlpha"; // For alphabetical sorting
import ClearIcon from "@mui/icons-material/Clear"; // Clear filters icon
import Tooltip from "@mui/material/Tooltip";
import { Select, MenuItem, FormControl, InputLabel, Pagination, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const ProductListing = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [products, setProducts] = useState([]);
  const [originalProducts, setOriginalProducts] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [productTypeFilter, setProductTypeFilter] = useState("");
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const history = useHistory();
  const auth = localStorage.getItem("authToken");

  const ITEMS_PER_PAGE = 20;
  const searchParams = new URLSearchParams(location.search);
  const category = searchParams.get("category") || "";
  // Map URL category tag to full display name (matches ProductList nameToCategoryTag)
  const categoryTagToDisplayName = {
    Exclusive: "Exclusively Available With Us",
    "Coming Soon": "Coming Soon",
    Combo: "Combinations Titles",
    Rare: "Rare Titles",
    "On Discount": "On Discount/Special Offer",
    Compilations: "Compilations Titles",
    Prebooking: "Prebooking",
    Bestseller: "Bestseller",
    "Latest Releases": "Latest Releases",
    "Preowned and Old Pressings": "Preowned and Old Pressings",
    "Recently Added": "Recently Added",
    "Rare & Combos": "Rare & Combos",
    "Prebooking and Latest Releases": "Prebooking and Latest Releases",
    "Premium Titles": "Premium Titles",
  };
  const categoryDisplayName = categoryTagToDisplayName[category] ?? category;
  const currentPage = Math.max(1, parseInt(searchParams.get("page"), 10) || 1);
  const totalPages = Math.max(1, Math.ceil(products.length / ITEMS_PER_PAGE));
  const pageClamped = Math.min(currentPage, totalPages);
  const paginatedProducts = products.slice(
    (pageClamped - 1) * ITEMS_PER_PAGE,
    pageClamped * ITEMS_PER_PAGE
  );
  const goToPage = (page) => {
    const params = new URLSearchParams(location.search);
    params.set("page", String(page));
    history.push(`${location.pathname}?${params.toString()}`);
  };

  useEffect(() => {
    getProducts(category);
  }, [category]);

  const getProducts = async (tag) => {
    try {
      setLoading(true);
      let requestData = { tag: category || undefined };

      if (!category) {
        requestData = {};
      } else if (category === "Recently Added") {
        requestData = { tag: "", combo: "", count: 200 };
      } else if (category === "Rare" || category === "Rare & Combos") {
        requestData = { tag: "Rare", combo: "true" };
      } else if (category === "Combo") {
        requestData = { tag: "Combo", combo: "true" };
      } else if (category === "Compilations") {
        requestData = { category: "Compilation" };
      } else if (category === "Pre-Owned and Old Pressings") {
        requestData = { condition_type: "Preowned" };
      } else if (category === "Premium Titles") {
        requestData = { tag: "Premium" };
      }
      const res = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/product/getallproduct`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${auth}`,
          },
        }
      );

      if (res.data.success) {
        const productList = Object.values(res.data.data).map((item) => item);
        productList.sort((a, b) =>
          Number((a.qty_available ?? 0) <= 0) - Number((b.qty_available ?? 0) <= 0)
        );
        setProducts(productList);
        setOriginalProducts(productList);
        // Only set page=1 when URL has no page param (e.g. first load or new category), so back button history is preserved
        if (!searchParams.get("page")) {
          const params = new URLSearchParams(location.search);
          params.set("page", "1");
          history.replace(`${location.pathname}?${params.toString()}`);
        }
      } else {
        if (res.data.data === "no record found") {
          enqueueSnackbar(res.data.data, { variant: "info" });
        } else {
          console.log("Error Occurred");
          enqueueSnackbar(res.data.message, { variant: "error" });
        }
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar("Error occurred. Please try again.", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  // Filter change handlers
  const handleCategoryChange = (e) => setCategoryFilter(e.target.value);
  const handleLanguageChange = (e) => setLanguageFilter(e.target.value);
  const handleProductTypeChange = (e) => setProductTypeFilter(e.target.value);

  const inStockFirst = (a, b) =>
    Number((a.qty_available ?? 0) <= 0) - Number((b.qty_available ?? 0) <= 0);

  // Sync products from originalProducts when data loads (e.g. when returning from product detail)
  useEffect(() => {
    if (originalProducts.length === 0) return;
    const sorted = [...originalProducts].sort(inStockFirst);
    setProducts(sorted);
  }, [originalProducts]);

  // Function to sort products (in-stock first, then sold out last; then by price/alpha)
  const sortProducts = (order, type = "price") => {
    let sortedProducts = [...products].sort((a, b) => {
      const stockOrder = inStockFirst(a, b);
      if (stockOrder !== 0) return stockOrder;
      if (type === "price") {
        return order === "asc"
          ? a.selling_price - b.selling_price
          : b.selling_price - a.selling_price;
      }
      return order === "asc"
        ? a.title.localeCompare(b.title)
        : b.title.localeCompare(a.title);
    });
    setProducts(sortedProducts);
    const params = new URLSearchParams(location.search);
    params.set("page", "1");
    history.replace(`${location.pathname}?${params.toString()}`);
  };

  // Function to reset all filters
  const clearFilters = () => {
    setCategoryFilter("");
    setLanguageFilter("");
    setProductTypeFilter("");
    setProducts([...originalProducts]);
    const params = new URLSearchParams(location.search);
    params.set("page", "1");
    history.replace(`${location.pathname}?${params.toString()}`);
  };
  // New API to apply filters
  const applyFilters = async () => {
    try {
      setLoading(true);

      // Dynamically build the requestData based on selected filters
      let requestData = {};
      if (categoryFilter) requestData.category = categoryFilter;
      if (languageFilter) requestData.language = languageFilter;
      if (productTypeFilter) requestData.product_type = productTypeFilter;
      if (category) requestData.tag = category;
      if (category === "Rare" || category === "Rare & Combos") {
        requestData.tag = "Rare";
        requestData.combo = "true";
      }
      if (category === "Combo") {
        requestData.tag = "Combo";
        requestData.combo = "true";
      }
      if (category === "Pre-Owned and Old Pressings") {
        requestData.condition_type = "Preowned";
        delete requestData.tag;
      }
      if (category === "Compilations") {
        requestData.category = "Compilation";
        delete requestData.tag;
      }
      if (category === "Prebooking and Latest Releases") {
        requestData.tag = "Prebooking";
      }
      if (category === "Recently Added") {
        requestData.tag = null;
      }
      const res = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/product/getfilterproducts`,
        requestData,
        {
          headers: { Authorization: `Bearer ${auth}` },
        }
      );

      if (res.data.success) {
        const list = Object.values(res.data.data);
        list.sort((a, b) =>
          Number((a.qty_available ?? 0) <= 0) - Number((b.qty_available ?? 0) <= 0)
        );
        setProducts(list);
        const params = new URLSearchParams(location.search);
        params.set("page", "1");
        history.replace(`${location.pathname}?${params.toString()}`);
      } else {
        setProducts([]);
        // enqueueSnackbar(res.data.message || "No records found", {
        //   variant: "info",
        // });
      }
    } catch (error) {
      enqueueSnackbar("Error occurred. Please try again.", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  // Only run applyFilters when user has selected at least one filter.
  // When all filters are empty, keep the list from getProducts (getallproduct) so tag-based
  // categories like "Coming Soon" show products instead of being overwritten by getfilterproducts.
  useEffect(() => {
    const hasFilter = categoryFilter || languageFilter || productTypeFilter;
    if (hasFilter) {
      applyFilters();
    }
  }, [categoryFilter, languageFilter, productTypeFilter]);

  return (
    <PageWrapper>
      <div>
        <h2 className="text-2xl font-bold mb-5">{categoryDisplayName}</h2>
        <BackButton />

        {/* Filter Select Fields */}
        <div className="flex flex-wrap justify-end gap-2 mb-4">
          {/* <div className="flex justify-start gap-4 mb-4"> */}
          {/* Category Filter */}
          <FormControl variant="outlined" sx={{
            width: {
              xs: "100%",  // Full width on mobile (xs)
              sm: "100%",  // Full width on tablets (sm)
              md: "120px",  // Auto width on desktops (md and up)
            },
          }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              onChange={handleCategoryChange}
              label="Category"
            >
              <MenuItem value={""}>All</MenuItem>
              <MenuItem value={"Movie Sound Track"}>
                Movie Sound Track
              </MenuItem>
              <MenuItem value={"Compilation"}>Compilation</MenuItem>
              <MenuItem value={"Album"}>Album</MenuItem>
              {/* <MenuItem value={"Other"}>Other</MenuItem> */}
            </Select>
          </FormControl>

          {/* Language Filter */}
          <FormControl variant="outlined" sx={{
            width: {
              xs: "100%",  // Full width on mobile (xs)
              sm: "100%",  // Full width on tablets (sm)
              md: "120px",  // Auto width on desktops (md and up)
            },
          }}>
            <InputLabel>Language</InputLabel>
            <Select
              value={languageFilter}
              onChange={handleLanguageChange}
              label="Language"
            >
              <MenuItem value={""}>All</MenuItem>
              <MenuItem value={"Hindi"}>Hindi</MenuItem>
              <MenuItem value={"English"}>English</MenuItem>
              <MenuItem value={"Marathi"}>Marathi</MenuItem>
              <MenuItem value={"Urdu"}>Urdu</MenuItem>
              <MenuItem value={"Punjabi"}>Punjabi</MenuItem>
              <MenuItem value={"Bengali"}>Bengali</MenuItem>
              <MenuItem value={"Tamil"}>Tamil</MenuItem>
              <MenuItem value={"Telugu"}>Telugu</MenuItem>
              <MenuItem value={"Malayalam"}>Malayalam</MenuItem>
            </Select>
          </FormControl>

          {/* Product Type Filter */}
          <FormControl variant="outlined" sx={{
            width: {
              xs: "100%",  // Full width on mobile (xs)
              sm: "100%",  // Full width on tablets (sm)
              md: "140px",  // Auto width on desktops (md and up)
            },
          }}>
            <InputLabel>Product Type</InputLabel>
            <Select
              value={productTypeFilter}
              onChange={handleProductTypeChange}
              label="Product Type"
            >
              <MenuItem value={""}>All</MenuItem>
              {/*<MenuItem value={"Liquid"}>Liquid</MenuItem>*/}
              <MenuItem value={"CD"}>CD</MenuItem>
              <MenuItem value={"Cassette"}>Cassette</MenuItem>
              <MenuItem value={"Vinyl Record"}>Vinyl Record</MenuItem>
            </Select>
          </FormControl>
          {/* Sort and Filter Buttons */}
          {/* Price: Low to High */}
          <Tooltip title="Sort by Price: Low to High" placement="top" arrow>
            <IconButton
              onClick={() => sortProducts("asc", "price")}
              color="primary"
              aria-label="Sort Price Low to High"
            >
              <ArrowUpwardIcon />
            </IconButton>
          </Tooltip>

          {/* Price: High to Low */}
          <Tooltip title="Sort by Price: High to Low" placement="top" arrow>
            <IconButton
              onClick={() => sortProducts("desc", "price")}
              color="primary"
              aria-label="Sort Price High to Low"
            >
              <ArrowDownwardIcon />
            </IconButton>
          </Tooltip>

          {/* Alphabetical Sorting */}
          <Tooltip title="Sort Alphabetically: A-Z" placement="top" arrow>
            <IconButton
              onClick={() => sortProducts("asc", "alphabet")}
              color="primary"
              aria-label="Sort Alphabetically A-Z"
            >
              <SortByAlphaIcon />
            </IconButton>
          </Tooltip>

          {/* Clear All Filters */}
          <Tooltip title="Clear All Filters" placement="top" arrow>
            <IconButton
              onClick={clearFilters}
              color="secondary"
              aria-label="Clear All Filters"
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        </div>

        {products.length > 0 ? (
          <>
            <main className="relative grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 items-stretch grid-auto-rows-minmax(26rem, auto) sm:grid-auto-rows-minmax(28rem, auto) md:grid-auto-rows-minmax(30rem, auto) xl:grid-auto-rows-minmax(32rem, auto)">
              {paginatedProducts.map((product) => (
                <div key={product.product_id} className="h-full min-h-0">
                  <SingleProduct
                    key={product.product_id}
                    product={{
                      _id: product.product_id,
                      qty: 0,
                      name: product.title,
                      description: product.description,
                      brand: product.category,
                      category: "Sports", // Replace with actual category if available
                      gender: "Men", // Replace with actual gender if available
                      weight: "200g", // Replace with actual weight if available
                      quantity: product.qty_available, // Replace with actual quantity if available
                      image: product.product_images[0],
                      rating: 4.5, // Replace with actual rating if available
                      price: product.selling_price,
                      newPrice: product.selling_price,
                      trending: true, // Replace with actual trending status if available
                      label: product.label,
                      type: product.product_type,
                      condition_type: product.condition_type,
                      is_courier_charge_free: product.is_courier_charge_free,
                      tag: product.tag,
                    }}
                  />
                </div>
              ))}
            </main>
            {totalPages > 1 && (
              <div className="flex justify-center mt-8 px-2 overflow-x-auto">
                <Pagination
                  count={totalPages}
                  page={pageClamped}
                  onChange={(e, value) => goToPage(value)}
                  variant="outlined"
                  shape="rounded"
                  size={isMobile ? "small" : "medium"}
                  showFirstButton={!isMobile}
                  showLastButton={!isMobile}
                  siblingCount={1}
                  boundaryCount={isMobile ? 1 : 1}
                  sx={{
                    "& .MuiPagination-ul": {
                      flexWrap: "wrap",
                      justifyContent: "center",
                      gap: isMobile ? "4px" : "8px",
                    },
                    "& .MuiPaginationItem-root": {
                      minWidth: isMobile ? 32 : 40,
                      height: isMobile ? 32 : 40,
                      fontSize: isMobile ? "0.8125rem" : "0.875rem",
                      borderRadius: "8px",
                      fontWeight: 600,
                      "&:hover": {
                        backgroundColor: "rgba(217, 119, 6, 0.12)",
                        borderColor: "#d97706",
                      },
                    },
                    "& .Mui-selected": {
                      backgroundColor: "#b45309",
                      borderColor: "#b45309",
                      color: "#1f2937",
                      "&:hover": {
                        backgroundColor: "#92400e",
                        borderColor: "#92400e",
                      },
                    },
                    "& .MuiPaginationItem-icon": {
                      fontSize: isMobile ? "1.125rem" : "1.25rem",
                    },
                  }}
                />
              </div>
            )}
          </>
        ) : (
          <p className="font-sans text-4xl font-bold uppercase tracking-wide text-gray-300 text-center w-full py-32">
            Nothing to Show!
          </p>
        )}
      </div>
    </PageWrapper>
  );
};

export default ProductListing;
